'use client';

import { useCallback, useState } from 'react';
import { Button, Flex, Icon, Input, Text } from '@chakra-ui/react';
import { MdCheckCircle, MdOutlineNotificationsActive } from 'react-icons/md';
import { api } from '@/services/api';
import { toaster } from '../ui/toaster';

type WaitlistFormProps = {
  productId: string;
  productOptId?: string | null;
  /** "launch" = produto ainda por lançar (em vez de esgotado). */
  variant?: 'restock' | 'launch';
};

const WaitlistForm = ({
  productId,
  productOptId = null,
  variant = 'restock',
}: WaitlistFormProps) => {
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (isSaving) return;

      setIsSaving(true);
      try {
        await api.post('/waitlist', {
          email,
          product_id: productId,
          product_opt_id: productOptId,
        });
        setSubmitted(true);
      } catch (err) {
        const message =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          'Não foi possível concluir o pedido. Tenta novamente.';
        toaster.create({ type: 'error', title: message });
      } finally {
        setIsSaving(false);
      }
    },
    [email, productId, productOptId, isSaving],
  );

  if (submitted) {
    return (
      <Flex
        align="center"
        justify="center"
        gap="8px"
        py="10px"
        px="12px"
        mt="10px"
        borderRadius="12px"
        bg={{ base: 'rgba(34,197,94,0.06)', _dark: 'rgba(34,197,94,0.1)' }}
        border="1px solid rgba(34,197,94,0.2)"
      >
        <Icon fontSize="16px" color="rgba(22,163,74,0.9)" flexShrink={0}>
          <MdCheckCircle />
        </Icon>
        <Text
          fontSize="13px"
          fontWeight="500"
          color={{ base: 'rgba(0,0,0,0.68)', _dark: 'rgba(255,255,255,0.75)' }}
        >
          Ficas avisado! Enviamos um email assim que estiver disponível.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit}
      flexDir="column"
      gap="8px"
      mt="10px"
      py="12px"
      px="12px"
      borderRadius="12px"
      bg={{ base: 'rgba(0,176,202,0.04)', _dark: 'rgba(0,176,202,0.06)' }}
      border="1px solid rgba(0,176,202,0.18)"
    >
      <Flex align="center" gap="6px">
        <Icon fontSize="15px" color="#00B0CA" flexShrink={0}>
          <MdOutlineNotificationsActive />
        </Icon>
        <Text
          fontSize="13px"
          fontWeight="600"
          color={{ base: 'rgba(0,0,0,0.75)', _dark: 'rgba(255,255,255,0.8)' }}
        >
          {variant === 'launch'
            ? 'Avisa-me quando estiver disponível'
            : 'Avisa-me quando voltar ao stock'}
        </Text>
      </Flex>
      <Flex gap="8px">
        <Input
          type="email"
          required
          placeholder="O teu email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fontSize="14px"
          h="38px"
          borderRadius="8px"
          bg={{ base: '#FFFFFF', _dark: '#0D2035' }}
          border="1px solid"
          borderColor={{ base: 'rgba(0,0,0,0.12)', _dark: 'rgba(255,255,255,0.12)' }}
          _focus={{ borderColor: '#00B0CA', boxShadow: '0 0 0 3px rgba(0,176,202,0.12)', outline: 'none' }}
        />
        <Button
          type="submit"
          loading={isSaving}
          loadingText="A enviar"
          bg="#00B0CA"
          color="#fff"
          fontSize="13px"
          fontWeight="700"
          h="38px"
          px="16px"
          borderRadius="8px"
          flexShrink={0}
          _hover={{ bg: '#009AB5' }}
        >
          Avisa-me
        </Button>
      </Flex>
    </Flex>
  );
};

export default WaitlistForm;
